import type { NotificationCategory } from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/** ADM-04's data layer. */

const KEY = ['notifications'] as const;
export const NOTIFICATION_SUMMARY_KEY = [...KEY, 'summary'] as const;

/**
 * The bell's dot (BR-ADM-04-01). Its own tiny query, deliberately: the shell
 * draws on every screen for every account, and making it fetch a page of rows
 * to decide whether to paint a 7-pixel dot would be the most-called request in
 * the product returning the most data it does not use.
 *
 * `refetchInterval` is a MINUTE, and the number is argued rather than picked.
 * A notification is not a chat message: §11's events are cheques clearing and
 * invoices ageing, and the sweep behind most of them runs hourly. A minute is
 * already far finer than the events themselves, and it costs one indexed count
 * per user per minute against a partial index built for exactly that read.
 * Sockets would buy nothing this screen needs and would add a second transport
 * to the deployment.
 */
export function useNotificationSummary() {
  return useQuery({
    queryKey: NOTIFICATION_SUMMARY_KEY,
    queryFn: async () => {
      const res = await api.notifications.summary();
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    refetchInterval: 60_000,
    // The bell is not worth a retry storm on a flaky connection: a dot that is
    // a minute stale is invisible, and ERR-ADM-04-05's sentence belongs on the
    // PAGE, where somebody is actually looking.
    retry: 1,
  });
}

/**
 * One chip's list. `pages` accumulates in the caller rather than here, because
 * §18's "Load more, 25 at a time" is an append, and a query key that changed
 * on every page would drop the reader back to the top of a list they were
 * halfway down.
 */
export function useNotifications(input: { category?: NotificationCategory; before?: string }) {
  return useQuery({
    queryKey: [...KEY, 'list', input.category ?? 'all', input.before ?? ''],
    queryFn: async () => {
      const res = await api.notifications.list({
        query: {
          ...(input.category ? { category: input.category } : {}),
          ...(input.before ? { before: input.before } : {}),
        },
      });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

/** BR-ADM-04-07 — scoped to the chosen chip, and asking no confirmation. */
export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (category?: NotificationCategory) => {
      const res = await api.notifications.markAllRead({ body: category ? { category } : {} });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * BR-ADM-04-06 — opening marks read AND says where to go. One call, because
 * the two are one act: a client that marked read and then navigated could
 * leave a row read with the reader stranded on the list.
 */
export function useOpenNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.notifications.open({ params: { id }, body: {} });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
}
